// client/src/components/media/NativeCameraCapture.jsx
// Component for capturing images and videos using the device's native camera application
// Uses Permissions API + getUserMedia for proper permission handling before launching native camera

import { useState, useRef, useEffect, useCallback } from 'react';
import { FaCamera, FaVideo, FaTimes, FaCheckCircle, FaRedo, FaLock } from 'react-icons/fa';
import './NativeCameraCapture.css';

/**
 * NativeCameraCapture Component
 *
 * Industry-standard camera workflow:
 * 1. Check camera permission status on mount
 * 2. If not granted, request permission via getUserMedia before opening native camera
 * 3. Open native camera app via file input with capture attribute
 * 4. Preview and confirm captured media
 *
 * Props:
 * @param {Function} onCapture - Callback when media is captured: (files: File[]) => void
 * @param {Function} onClose - Callback when component is closed
 * @param {String} mode - 'photo' | 'video' | 'both' - What type of media to capture (default: 'both')
 * @param {Boolean} multiple - Allow multiple captures (default: false)
 * @param {Number} maxFiles - Maximum number of files to capture (default: 10)
 * @param {Number} maxImageSizeMB - Maximum image size in MB (default: 10)
 * @param {Number} maxVideoSizeMB - Maximum video size in MB (default: 100)
 */
const NativeCameraCapture = ({
  onCapture,
  onClose,
  mode = 'both',
  multiple = false,
  maxFiles = 10,
  maxImageSizeMB = 10,
  maxVideoSizeMB = 100
}) => {
  const [capturedFiles, setCapturedFiles] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [error, setError] = useState('');
  const [captureInProgress, setCaptureInProgress] = useState(false);

  // Permission states: 'checking' | 'granted' | 'prompt' | 'denied' | 'unsupported'
  const [cameraPermission, setCameraPermission] = useState('checking');
  const [requestingPermission, setRequestingPermission] = useState(false);

  const photoInputRef = useRef(null);
  const videoInputRef = useRef(null);

  /**
   * Check camera permission status using the Permissions API
   * Falls back to 'prompt' if Permissions API is not available
   */
  const checkPermission = useCallback(async () => {
    try {
      // Check if mediaDevices is available (requires HTTPS or localhost)
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        // On mobile browsers, file input with capture still works without mediaDevices
        // This is common in older WebViews or non-HTTPS contexts
        setCameraPermission('unsupported');
        return;
      }

      // Try the Permissions API first (supported in Chrome, Edge, Firefox)
      if (navigator.permissions && navigator.permissions.query) {
        try {
          const result = await navigator.permissions.query({ name: 'camera' });
          setCameraPermission(result.state); // 'granted', 'prompt', or 'denied'

          // Listen for permission changes (e.g., user changes in device settings)
          result.addEventListener('change', () => {
            setCameraPermission(result.state);
          });
          return;
        } catch {
          // Permissions API doesn't support 'camera' query in some browsers (Safari)
          // Fall through to default
        }
      }

      // If Permissions API not available, assume 'prompt' (user hasn't decided yet)
      // The browser will prompt when getUserMedia is called
      setCameraPermission('prompt');
    } catch {
      setCameraPermission('prompt');
    }
  }, []);

  /**
   * Request camera permission by calling getUserMedia
   * This triggers the browser's native permission dialog
   * The stream is immediately stopped - we only need the permission grant
   */
  const requestPermission = useCallback(async () => {
    setRequestingPermission(true);
    setError('');

    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        // If getUserMedia is unavailable, try opening native camera directly
        // Mobile browsers often handle permissions at the OS level via file input
        setCameraPermission('granted');
        return;
      }

      // Request camera access - this triggers the browser's permission prompt
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });

      // Immediately stop all tracks - we only needed the permission grant
      stream.getTracks().forEach(track => track.stop());

      setCameraPermission('granted');
    } catch (err) {
      console.error('Camera permission request failed:', err);

      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setCameraPermission('denied');
        setError('Camera access was denied. Please enable camera permissions in your device settings.');
      } else if (err.name === 'NotFoundError') {
        setError('No camera found on this device.');
        setCameraPermission('denied');
      } else if (err.name === 'NotReadableError') {
        // Camera is in use by another app - permission may still be granted
        setCameraPermission('granted');
      } else {
        setError('Unable to access camera: ' + (err.message || 'Unknown error'));
        setCameraPermission('prompt');
      }
    } finally {
      setRequestingPermission(false);
    }
  }, []);

  // Check permission on mount
  useEffect(() => {
    checkPermission();
  }, [checkPermission]);

  /**
   * Handle file selection from native camera
   */
  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files || []);

    if (!files.length) {
      setCaptureInProgress(false);
      return;
    }

    setError('');
    const validFiles = [];
    const newPreviewUrls = [];
    const errorMessages = [];

    if (capturedFiles.length + files.length > maxFiles) {
      errorMessages.push(`Maximum ${maxFiles} files allowed. Only processing first ${maxFiles - capturedFiles.length} files.`);
    }

    const filesToProcess = files.slice(0, maxFiles - capturedFiles.length);

    filesToProcess.forEach((file) => {
      const isImg = file.type.startsWith('image/');
      const isVid = file.type.startsWith('video/');
      const sizeMB = file.size / (1024 * 1024);

      if (mode === 'photo' && !isImg) {
        errorMessages.push(`${file.name}: Only images are allowed in photo mode`);
        return;
      }
      if (mode === 'video' && !isVid) {
        errorMessages.push(`${file.name}: Only videos are allowed in video mode`);
        return;
      }
      if (!isImg && !isVid) {
        errorMessages.push(`${file.name}: Invalid file type`);
        return;
      }

      if (isImg && sizeMB > maxImageSizeMB) {
        errorMessages.push(`${file.name}: Image too large (max ${maxImageSizeMB}MB)`);
        return;
      }
      if (isVid && sizeMB > maxVideoSizeMB) {
        errorMessages.push(`${file.name}: Video too large (max ${maxVideoSizeMB}MB)`);
        return;
      }

      validFiles.push(file);
      newPreviewUrls.push({
        url: URL.createObjectURL(file),
        type: isImg ? 'image' : 'video',
        name: file.name,
        size: file.size
      });
    });

    if (validFiles.length > 0) {
      if (multiple) {
        setCapturedFiles(prev => [...prev, ...validFiles]);
        setPreviewUrls(prev => [...prev, ...newPreviewUrls]);
      } else {
        previewUrls.forEach(preview => URL.revokeObjectURL(preview.url));
        setCapturedFiles(validFiles);
        setPreviewUrls(newPreviewUrls);
      }
    }

    if (errorMessages.length > 0) {
      setError(errorMessages.join('. '));
    }

    setCaptureInProgress(false);
    event.target.value = '';
  };

  /**
   * Trigger native camera - checks permission first
   */
  const triggerCamera = async (type) => {
    setError('');
    setCaptureInProgress(true);

    // If permission hasn't been granted yet, request it first
    if (cameraPermission === 'prompt') {
      await requestPermission();
      // After requesting, check updated state - if denied, stop
      // Note: requestPermission updates cameraPermission state,
      // but we need to check the result directly since state update is async
    }

    try {
      if (type === 'photo' && photoInputRef.current) {
        photoInputRef.current.click();
      } else if (type === 'video' && videoInputRef.current) {
        videoInputRef.current.click();
      }
    } catch (err) {
      console.error('Error triggering camera:', err);
      setError('Failed to open camera. Please try again.');
      setCaptureInProgress(false);
    }
  };

  const removeFile = (index) => {
    if (previewUrls[index]?.url) {
      URL.revokeObjectURL(previewUrls[index].url);
    }
    setCapturedFiles(capturedFiles.filter((_, i) => i !== index));
    setPreviewUrls(previewUrls.filter((_, i) => i !== index));
  };

  const handleConfirm = () => {
    if (capturedFiles.length === 0) {
      setError('Please capture at least one photo or video');
      return;
    }
    onCapture(capturedFiles);
  };

  const handleRetry = () => {
    previewUrls.forEach(preview => URL.revokeObjectURL(preview.url));
    setCapturedFiles([]);
    setPreviewUrls([]);
    setError('');
  };

  // Cleanup object URLs on unmount
  useEffect(() => {
    return () => {
      previewUrls.forEach(preview => {
        if (preview?.url) URL.revokeObjectURL(preview.url);
      });
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Determine if camera buttons should be shown
  const canCapture = cameraPermission === 'granted' || cameraPermission === 'prompt' || cameraPermission === 'unsupported';
  const showPermissionRequest = cameraPermission === 'prompt' && capturedFiles.length === 0;
  const showDenied = cameraPermission === 'denied';

  return (
    <div className="native-camera-capture">
      <div className="native-camera-overlay" onClick={onClose} />

      <div className="native-camera-content">
        {/* Header */}
        <div className="native-camera-header">
          <h3 className="native-camera-title">
            {mode === 'photo' ? 'Capture Photo' : mode === 'video' ? 'Capture Video' : 'Capture Media'}
          </h3>
          <button
            className="native-camera-close-btn"
            onClick={onClose}
            aria-label="Close"
          >
            <FaTimes />
          </button>
        </div>

        {/* Permission checking state */}
        {cameraPermission === 'checking' && (
          <div className="native-camera-instructions">
            <p>Checking camera permissions...</p>
          </div>
        )}

        {/* Permission request prompt - shown before first use */}
        {showPermissionRequest && !captureInProgress && (
          <div className="native-camera-instructions">
            <div className="native-camera-permission-prompt">
              <FaCamera style={{ fontSize: '2rem', color: '#ff6800', marginBottom: '0.75rem' }} />
              <p>
                {mode === 'both'
                  ? 'This app needs access to your camera to capture photos and videos.'
                  : mode === 'photo'
                  ? 'This app needs access to your camera to take photos.'
                  : 'This app needs access to your camera to record videos.'}
              </p>
              <p className="text-gray-400" style={{ marginTop: '0.5rem' }}>
                Tap a button below to allow camera access and start capturing.
              </p>
            </div>
          </div>
        )}

        {/* Permission granted - show instructions */}
        {cameraPermission === 'granted' && capturedFiles.length === 0 && !captureInProgress && (
          <div className="native-camera-instructions">
            <p>
              {mode === 'both'
                ? 'Choose whether to capture a photo or video using your device\'s camera'
                : mode === 'photo'
                ? 'Tap the button below to open your device\'s camera and take a photo'
                : 'Tap the button below to open your device\'s camera and record a video'
              }
            </p>
            {multiple && (
              <p className="text-gray-400">You can capture up to {maxFiles} files</p>
            )}
          </div>
        )}

        {/* Unsupported - file input will still work on most mobile browsers */}
        {cameraPermission === 'unsupported' && capturedFiles.length === 0 && !captureInProgress && (
          <div className="native-camera-instructions">
            <p>Tap a button below to open your device's camera.</p>
            {multiple && (
              <p className="text-gray-400">You can capture up to {maxFiles} files</p>
            )}
          </div>
        )}

        {/* Permission denied */}
        {showDenied && (
          <div className="native-camera-error">
            <p><FaLock style={{ marginRight: '0.5rem' }} />Camera access was denied.</p>
            <div className="native-camera-permission-help">
              <p><strong>To enable camera access:</strong></p>
              <ul>
                <li>Go to your device Settings</li>
                <li>Find this browser in your apps list</li>
                <li>Enable Camera permissions</li>
                <li>Return here and try again</li>
              </ul>
              <button
                className="native-camera-btn native-camera-btn-photo"
                onClick={() => {
                  setCameraPermission('prompt');
                  setError('');
                }}
                style={{ marginTop: '1rem', width: '100%' }}
              >
                Try Again
              </button>
            </div>
          </div>
        )}

        {/* Error Display */}
        {error && !showDenied && (
          <div className="native-camera-error">
            <p>{error}</p>
          </div>
        )}

        {/* Preview Area */}
        {previewUrls.length > 0 && (
          <div className="native-camera-preview-area">
            <h4>Captured Media ({capturedFiles.length})</h4>
            <div className="native-camera-preview-grid">
              {previewUrls.map((preview, index) => (
                <div key={index} className="native-camera-preview-item">
                  {preview.type === 'image' ? (
                    <img
                      src={preview.url}
                      alt={`Capture ${index + 1}`}
                      className="native-camera-preview-image"
                    />
                  ) : (
                    <video
                      src={preview.url}
                      controls
                      playsInline
                      className="native-camera-preview-video"
                    />
                  )}
                  <button
                    className="native-camera-remove-btn"
                    onClick={() => removeFile(index)}
                    aria-label="Remove"
                  >
                    <FaTimes />
                  </button>
                  <div className="native-camera-preview-info">
                    <span className="file-type">{preview.type}</span>
                    <span className="file-size">
                      {(preview.size / (1024 * 1024)).toFixed(2)} MB
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="native-camera-actions">
          {canCapture && (capturedFiles.length === 0 || (multiple && capturedFiles.length < maxFiles)) ? (
            <div className="native-camera-capture-buttons">
              {(mode === 'photo' || mode === 'both') && (
                <button
                  className="native-camera-btn native-camera-btn-photo"
                  onClick={() => triggerCamera('photo')}
                  disabled={captureInProgress || requestingPermission}
                >
                  <FaCamera />
                  <span>{requestingPermission ? 'Requesting Access...' : 'Take Photo'}</span>
                </button>
              )}

              {(mode === 'video' || mode === 'both') && (
                <button
                  className="native-camera-btn native-camera-btn-video"
                  onClick={() => triggerCamera('video')}
                  disabled={captureInProgress || requestingPermission}
                >
                  <FaVideo />
                  <span>{requestingPermission ? 'Requesting Access...' : 'Record Video'}</span>
                </button>
              )}
            </div>
          ) : null}

          {capturedFiles.length > 0 && (
            <div className="native-camera-confirm-buttons">
              <button
                className="native-camera-btn native-camera-btn-retry"
                onClick={handleRetry}
              >
                <FaRedo />
                <span>Retry</span>
              </button>
              <button
                className="native-camera-btn native-camera-btn-confirm"
                onClick={handleConfirm}
              >
                <FaCheckCircle />
                <span>Use {capturedFiles.length} {capturedFiles.length === 1 ? 'File' : 'Files'}</span>
              </button>
            </div>
          )}

          {multiple && capturedFiles.length > 0 && capturedFiles.length < maxFiles && (
            <div className="native-camera-add-more">
              <p className="text-gray-400">
                {maxFiles - capturedFiles.length} more {maxFiles - capturedFiles.length === 1 ? 'file' : 'files'} available
              </p>
            </div>
          )}
        </div>

        {/* Hidden file inputs - trigger native camera */}
        {(mode === 'photo' || mode === 'both') && (
          <input
            ref={photoInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            multiple={multiple}
            onChange={handleFileSelect}
            style={{ display: 'none' }}
            aria-label="Capture photo"
          />
        )}

        {(mode === 'video' || mode === 'both') && (
          <input
            ref={videoInputRef}
            type="file"
            accept="video/*"
            capture="environment"
            multiple={multiple}
            onChange={handleFileSelect}
            style={{ display: 'none' }}
            aria-label="Capture video"
          />
        )}
      </div>
    </div>
  );
};

export default NativeCameraCapture;
